/**
 * Flight operations · delays.
 *
 * Every delayed movement with the story of its delay as the airport heard
 * it: who told us (the airline's operations control, the handler, an ATC
 * flow message, the schedule feed, the duty manager), when, what new time
 * they gave, and each revision since — so the question that decides whether
 * the stand plan survives, "how much warning did we get?", has an answer.
 *
 * Beneath it, what the delays do to the rest of the day: stands held longer
 * than planned running into the next movement, gates boarding late on top of
 * the next departure, each with a free stand for whoever is pushed out.
 *
 * A notice can be recorded here when a source tells somebody by phone or
 * message: it goes to that flight on the flight record, becomes its new
 * estimate and is kept with where it came from. Only that flight is written.
 */
import { useMemo, useState } from "react";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, EmptyState, Field, Input, Select } from "../../design/components";
import {
  delayImpacts,
  delayedStories,
  delaySummary,
  opsState,
  SOURCE_LABEL,
  WARNING_LABEL,
  type DelayNotice,
  type DelaySource,
  type Warning,
} from "../../domain/flightDelays";
import { flightName, type Flight } from "../../domain/flights";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { AERODROME_OFFSET_HOURS, hhmm as clockTime } from "../../lib/format";
import { Tiles } from "../executive/shared";
import { DayNotes, DayState, FlightTheme, hasDay, ImpactList, LiveStrip, OpsPill } from "./FlightTheme";
import s from "./Flights.module.css";
import { blocksFromWorkOrders } from "./standBlocks";
import { useFlightWorld } from "./useFlightWorld";
import { useWorkOrders } from "../../api/queries";

const hhmm = (iso: string | null | undefined) => clockTime(iso, "—");
const WARN_COLOUR: Record<Warning, string> = {
  early: "var(--ops-green)",
  short: "var(--ops-yellow)",
  after: "var(--ops-red)",
  none: "var(--ops-red)",
};

export function DelaysPage() {
  const session = useSession();
  const world = useFlightWorld();
  const flights = world.flights;
  const register = world.register;
  const orders = useWorkOrders();
  const blocks = useMemo(
    () => blocksFromWorkOrders(register.stands, orders.data?.items ?? []),
    [register.stands, orders.data],
  );
  const now = new Date();
  const [warning, setWarning] = useState<"" | Warning>("");
  const [source, setSource] = useState<"" | DelaySource>("");
  const [toCome, setToCome] = useState(false);

  const stories = useMemo(() => delayedStories(flights), [flights]);
  const summary = useMemo(() => delaySummary(stories), [stories]);
  const impacts = useMemo(() => delayImpacts(flights, register, blocks, new Date()), [flights, register, blocks]);
  const shown = stories.filter(
    (st) =>
      (!warning || st.warning === warning) &&
      (!source || st.notices.some((n) => n.source === source)) &&
      (!toCome || (!st.flight.actualAt && !st.flight.cancelled)),
  );
  const standOf = (f: Flight) => {
    const pair = f.turnaround ? flights.find((x) => x.turnaround === f.turnaround && x.id !== f.id) : undefined;
    const a =
      register.assignments.find((x) => x.flightId === f.id) ??
      (pair ? register.assignments.find((x) => x.flightId === pair.id) : undefined);
    const st = a ? register.stands.find((x) => x.code === a.standCode) : undefined;
    /* the departure's own gate where the allocation placed one; the stand's otherwise */
    const gate = f.gate ?? st?.gate;
    const parts = [st ? t("stand {s}", { s: st.code }) : null, gate ? t("gate {g}", { g: gate }) : null];
    return parts.filter(Boolean).join(" · ") || null;
  };

  useScreenContext({
    route: "/flight-delays",
    title: "Delays",
    summary: [
      t("{n} movements delayed today, {c} still to come.", { n: summary.delayed, c: summary.stillToCome }),
      t("{e} came with early warning; {x} reached us after the scheduled time or not at all.", {
        e: summary.early,
        x: summary.after + summary.none,
      }),
      t("{n} knock-ons on stands and gates.", { n: impacts.length }),
    ],
    offers: [
      { label: t("Which delays hit the stand plan?"), ask: "which delays are running into other flights' stands?" },
    ],
    worksInFull: true,
  });

  const tiles = [
    {
      key: "delayed",
      label: t("delayed today"),
      value: summary.delayed,
      unit: "",
      text: t("{n} still to come", { n: summary.stillToCome }),
      tone: summary.stillToCome ? ("warn" as const) : ("good" as const),
    },
    {
      key: "early",
      label: t("with early warning"),
      value: summary.early,
      unit: "",
      text: t("an hour or more before the scheduled time"),
      tone: "good" as const,
    },
    {
      key: "short",
      label: t("at short notice"),
      value: summary.short,
      unit: "",
      text: t("less than an hour before"),
      tone: summary.short ? ("warn" as const) : undefined,
    },
    {
      key: "late",
      label: t("after the fact or never"),
      value: summary.after + summary.none,
      unit: "",
      text: t("told after the scheduled time, or not told"),
      tone: summary.after + summary.none ? ("bad" as const) : ("good" as const),
    },
    {
      key: "lead",
      label: t("warning, on average"),
      value: summary.averageLead,
      unit: t("min"),
      text: t("between the first notice and the scheduled time"),
    },
    {
      key: "knock",
      label: t("knock-ons"),
      value: impacts.length,
      unit: "",
      text: t("on stands and gates"),
      tone: impacts.length ? ("bad" as const) : ("good" as const),
    },
  ];

  const lede = (
    <p className={s.lede}>
      {t(
        "Every delay, who reported it and when, and which stands and gates it affects.",
      )}
    </p>
  );
  if (!hasDay(world))
    return (
      <FlightTheme>
        <Page eyebrow={t("{site} · Flight operations", { site: session.site.code })} title="Delays">
          {lede}
          <DayState world={world} />
        </Page>
      </FlightTheme>
    );

  return (
    <FlightTheme>
      <Page eyebrow={t("{site} · Flight operations", { site: session.site.code })} title="Delays">
        <LiveStrip flights={flights} site={session.site.code} />
        {lede}
        <DayNotes world={world} />
        <Tiles
          items={tiles.map((x) => ({
            ...x,
            assumed: true,
            sources: [
              { kind: "entered" as const, ref: "flights", label: t("the day's movements and their delay notices") },
            ],
          }))}
          currency="SAR"
        />

        <Card title="Knock-on on stands and gates" sub="the plan as the delays now make it" testId="delay-knockon">
          <ImpactList impacts={impacts} empty="No delay is running into another movement's stand or gate." />
        </Card>

        <Card
          title="Delayed movements, and who told us"
          sub="When the airport was told, and each update"
          actions={
            <div className={s.filters}>
              <Field label="Warning">
                <Select value={warning} onChange={(e) => setWarning(e.target.value as Warning)}>
                  <option value="">{t("Any")}</option>
                  {(Object.keys(WARNING_LABEL) as Warning[]).map((w) => (
                    <option key={w} value={w}>
                      {t(WARNING_LABEL[w])}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Source">
                <Select value={source} onChange={(e) => setSource(e.target.value as DelaySource)}>
                  <option value="">{t("Any")}</option>
                  {(Object.keys(SOURCE_LABEL) as DelaySource[]).map((k) => (
                    <option key={k} value={k}>
                      {t(SOURCE_LABEL[k])}
                    </option>
                  ))}
                </Select>
              </Field>
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13.5 }}>
                <input type="checkbox" checked={toCome} onChange={(e) => setToCome(e.target.checked)} />
                {t("Still to come only")}
              </label>
            </div>
          }
        >
          {shown.length === 0 ? (
            <EmptyState>{t("No delayed movement matches.")}</EmptyState>
          ) : (
            <div className={s.stories} data-testid="delay-stories">
              {shown.map((st) => {
                const f = st.flight;
                const newTime = f.actualAt ?? f.estimatedAt ?? st.latest?.newTime;
                return (
                  <article
                    key={f.id}
                    className={s.story}
                    data-warning={st.warning}
                    data-testid={`delay-${flightName(f)}`}
                  >
                    <div className={s.storyHead}>
                      <span className={s.storyFlight}>{flightName(f)}</span>
                      <OpsPill state={opsState(f, now)} />
                      <span className={s.meta}>
                        {t(f.direction === "arrival" ? "from {p}" : "to {p}", { p: f.otherAirport ?? "—" })}
                        {standOf(f) ? ` · ${standOf(f)}` : ""}
                      </span>
                    </div>
                    <div className={s.times}>
                      <s>{hhmm(f.scheduledAt)}</s> → <b>{hhmm(newTime)}</b>
                      {st.minutes != null && (
                        <span className={st.minutes > 0 ? s.bad : s.good}>
                          {st.minutes > 0 ? `+${st.minutes}` : st.minutes} {t("min")}
                        </span>
                      )}
                      {f.actualAt && <span className={s.meta}>({t("actual")})</span>}
                    </div>
                    <div>
                      <span className={s.pill} style={{ ["--c" as string]: WARN_COLOUR[st.warning] }}>
                        {t(WARNING_LABEL[st.warning])}
                      </span>{" "}
                      {st.first && (
                        <span className={s.meta}>
                          {st.leadMinutes != null && st.leadMinutes >= 0
                            ? t("first told {m} min before, by {src}", {
                                m: st.leadMinutes,
                                src: t(SOURCE_LABEL[st.first.source]),
                              })
                            : t("first told {m} min after the scheduled time, by {src}", {
                                m: Math.abs(st.leadMinutes ?? 0),
                                src: t(SOURCE_LABEL[st.first.source]),
                              })}
                        </span>
                      )}
                    </div>
                    {st.notices.length > 0 ? (
                      <ul className={s.timeline}>
                        {st.notices.map((n, i) => (
                          <li key={i}>
                            <b className={s.mono}>{hhmm(n.at)}</b> {t(SOURCE_LABEL[n.source])}
                            {n.by && !n.by.startsWith(SOURCE_LABEL[n.source]) ? ` (${n.by})` : ""} —{" "}
                            {t("new time {t}", { t: hhmm(n.newTime) })}
                            {n.code ? ` · ${t("code {c}", { c: n.code })}` : ""}
                            {n.note ? ` · ${n.note}` : ""}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={s.bad} style={{ margin: 0 }}>
                        {t("No one reported this delay to the airport.")}
                      </p>
                    )}
                    {(f.delayCode || f.delayNote) && (
                      <div className={s.why}>
                        {f.delayCode && <span className={s.mono}>{f.delayCode}</span>}
                        {f.delayNote ? ` — ${f.delayNote}` : ""}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </Card>

        {summary.bySource.length > 0 && (
          <Card collapsible title="Where the notices came from" sub="how many notices each source sent today">
            <ul className={s.sources} data-testid="delay-sources">
              {summary.bySource.map((b) => {
                const most = Math.max(...summary.bySource.map((x) => x.count));
                return (
                  <li key={b.source} className={s.source}>
                    <span className={s.sourceName}>{t(SOURCE_LABEL[b.source])}</span>
                    <span className={s.sourceTrack}>
                      <span className={s.sourceFill} style={{ width: `${(b.count / most) * 100}%` }} />
                    </span>
                    <span className={s.barCount}>{b.count}</span>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}

        <RecordNotice flights={flights} onSave={world.recordNotice} />
      </Page>
    </FlightTheme>
  );
}

function RecordNotice({
  flights,
  onSave,
}: {
  flights: Flight[];
  onSave: (flightId: string, notice: DelayNotice) => Promise<void>;
}) {
  const open = flights.filter((f) => !f.actualAt && !f.cancelled && f.source !== "forecast");
  const [id, setId] = useState("");
  const [src, setSrc] = useState<DelaySource>("airline_occ");
  const [by, setBy] = useState("");
  const [time, setTime] = useState("");
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const f = open.find((x) => x.id === id);
  const ok = !!f && /^\d{2}:\d{2}$/.test(time);
  return (
    <Card
      title="Record a delay notice"
      sub="Log a delay reported by phone or message"
      testId="delay-record"
    >
      <form
        className={s.filters}
        onSubmit={(e) => {
          e.preventDefault();
          if (!f || !ok) return;
          const base = new Date(f.scheduledAt ?? new Date().toISOString());
          const [h, m] = time.split(":").map(Number);
          const local = new Date(base);
          /* the time as typed is the aerodrome's local time on the movement's own day */
          local.setUTCHours(h - AERODROME_OFFSET_HOURS, m, 0, 0);
          if (local.getTime() < base.getTime() - 6 * 3_600_000) local.setUTCDate(local.getUTCDate() + 1);
          /*
           * The record keeps who logged it, from the sign-in; the person who
           * phoned is kept in the note, so the name typed here is not lost.
           */
          const who = by.trim();
          const said = note.trim();
          const notice: DelayNotice = {
            at: new Date().toISOString(),
            source: src,
            newTime: local.toISOString(),
            code: code.trim() || undefined,
            note: who ? [said, t("from {p}", { p: who })].filter(Boolean).join(" · ") : said || undefined,
          };
          const label = t("{f}: new time {t} recorded, from {src}.", { f: flightName(f), t: time, src: t(SOURCE_LABEL[src]) });
          setBusy(true);
          setDone(null);
          setFailed(null);
          onSave(f.id, notice).then(
            () => {
              setDone(label);
              setTime("");
              setNote("");
            },
            (err: unknown) => setFailed(err instanceof Error ? err.message : String(err)),
          ).finally(() => setBusy(false));
        }}
      >
        <Field label="Movement">
          <Select value={id} onChange={(e) => setId(e.target.value)} data-testid="delay-rec-flight">
            <option value="">{t("Choose a movement")}</option>
            {open.map((x) => (
              <option key={x.id} value={x.id}>
                {flightName(x)} · {t(x.direction === "arrival" ? "Arrival" : "Departure")} ·{" "}
                {clockTime(x.scheduledAt, "")}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Source">
          <Select value={src} onChange={(e) => setSrc(e.target.value as DelaySource)}>
            {(Object.keys(SOURCE_LABEL) as DelaySource[]).map((k) => (
              <option key={k} value={k}>
                {t(SOURCE_LABEL[k])}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Who">
          <Input value={by} onChange={(e) => setBy(e.target.value)} placeholder={t("e.g. SV operations control")} />
        </Field>
        <Field label="New time (local)">
          <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="delay-rec-time" />
        </Field>
        <Field label="Delay code">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="93" style={{ width: 80 }} />
        </Field>
        <Field label="Note">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" disabled={!ok || busy} data-testid="delay-rec-save">
          {t("Record the notice")}
        </Button>
      </form>
      {done && <p className={s.good}>✓ {done}</p>}
      {failed && (
        <p className={s.bad} role="alert">
          {failed}
        </p>
      )}
    </Card>
  );
}
